import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input,
    OnChanges, Output, SimpleChanges, ViewChild
} from '@angular/core';
import {
    EmailAutomationServiceProxy, SetTaskProjectInput, TaskProjectCandidateDto
} from '@shared/service-proxies/service-proxies';

/**
 * Project REVIEW picker (G7 / sec 12.4) for the edit-task modal's Project field. When an email task's sender
 * is on MULTIPLE active projects the pipeline can't pick one, so it stores the candidates instead of guessing.
 * This shows the amber "N possible projects - tap to confirm" cue; picking one calls SetTaskProject (no-clobber:
 * links the task to the project + "Email" section and retires the candidates) and tells the host to reflect it.
 *
 * Self-contained: only renders for a task that has NO project yet AND has candidates - otherwise it emits
 * hasCandidatesChange(false) so the host keeps showing its normal project selector.
 */
@Component({
    selector: 'vf-task-project-review',
    templateUrl: './task-project-review.component.html',
    styleUrls: ['./task-project-review.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskProjectReviewComponent implements OnChanges {
    /** The cue button, so focus can be returned to it when the menu closes on Escape. */
    @ViewChild('cueButton') cueButton: ElementRef<HTMLButtonElement> | undefined;

    @Input() taskId: number | undefined;
    /** The task's current project id; when set, this picker is irrelevant (renders nothing). */
    @Input() projectId: number | undefined;

    /** A candidate was confirmed - the host should reflect the chosen project. */
    @Output() confirmed = new EventEmitter<{ projectId: number; projectName: string }>();
    /** Whether there are candidates to review (the host hides its normal selector while true). */
    @Output() hasCandidatesChange = new EventEmitter<boolean>();
    /** "Browse all projects" - hide the review cue and let the host show its normal project selector. */
    @Output() browseAll = new EventEmitter<void>();

    candidates: TaskProjectCandidateDto[] = [];

    /** True when at least one candidate carries real evidence (tier > 0). Precomputed rather than a getter
     *  so the template does no work per change-detection pass. When it is FALSE the backend ranked on bare
     *  recency, so NOTHING is labelled and nothing is dimmed - every row really is equally weak, and saying
     *  otherwise is the bug this whole surface was corrected for. */
    hasEvidence = false;
    open = false;
    saving = false;
    errorMessage = '';
    // Set once the user chooses "Browse all projects" - the cue is hidden and the host's normal selector takes over.
    browsing = false;

    constructor(
        private _email: EmailAutomationServiceProxy,
        private _cdr: ChangeDetectorRef,
        private _host: ElementRef<HTMLElement>
    ) {}

    /** Escape closes the menu. Without this a keyboard-only user could not dismiss it at all: the host
     *  modal is configured {keyboard: false}, so nothing above us acts on Escape either. markForCheck is
     *  required - the component is OnPush and a document listener runs outside its template CD path. */
    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (!this.open) {
            return;
        }

        this.close();

        if (this.cueButton) {
            this.cueButton.nativeElement.focus();
        }
    }

    /** Click-away. This replaced a full-screen `position: fixed` backdrop element, which could not do the
     *  job here: an ancestor (.task-slider) carries a transform, and a transformed ancestor becomes the
     *  containing block for fixed descendants - so `inset: 0` resolved to the slider, not the viewport, and
     *  clicks on the page outside the slider hit nothing. Containment on our own host is exact, and matches
     *  how the other dismissable panels in the edit-task modal work. */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.open) {
            return;
        }

        // The host element wraps BOTH the cue and the menu, so anything inside it is ours - including the
        // click that just opened the menu, which bubbles to document on the same tick.
        if (this._host.nativeElement.contains(event.target as Node)) {
            return;
        }

        this.close();
    }

    /** Project id is stable and unique within the offered set, so the rows are not torn down and
     *  rebuilt when the list is re-fetched after a confirm. */
    trackCandidate(index: number, candidate: TaskProjectCandidateDto): number {
        return candidate.projectId;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.taskId || changes.projectId) {
            this.candidates = [];
            this.hasEvidence = false;
            this.open = false;
            this.browsing = false;
            this.errorMessage = '';
            this.hasCandidatesChange.emit(false);
            // Candidates only matter while the task has no project assigned.
            if (this.taskId && !this.projectId) {
                this.load();
            }
        }
    }

    get count(): number {
        return this.candidates.length;
    }

    toggle(): void {
        this.open = !this.open;
    }

    /** The single close path. markForCheck matters because the two dismiss routes (Escape, click-away)
     *  are document listeners, which run outside this OnPush component's own change-detection path. */
    close(): void {
        this.open = false;
        this._cdr.markForCheck();
    }

    /** "Browse all projects" - hide this cue and hand off to the host's normal project selector. */
    onBrowseAll(): void {
        this.open = false;
        this.browsing = true;
        this.browseAll.emit();
    }

    confirm(candidate: TaskProjectCandidateDto): void {
        if (this.saving || !this.taskId || !candidate) {
            return;
        }
        this.saving = true;
        this.errorMessage = '';

        const input = new SetTaskProjectInput();
        input.taskId = this.taskId;
        input.projectId = candidate.projectId;

        this._email.setTaskProject(input).subscribe(
            res => {
                this.saving = false;
                if (res && res.responseData && !res.isError) {
                    this.open = false;
                    this.candidates = [];
                    this.hasCandidatesChange.emit(false);
                    this.confirmed.emit({ projectId: candidate.projectId, projectName: candidate.projectName });
                } else {
                    this.errorMessage = (res && res.errorMessage) || 'Could not set the project.';
                }
                this._cdr.markForCheck();
            },
            () => {
                this.saving = false;
                this.errorMessage = 'Could not set the project.';
                this._cdr.markForCheck();
            }
        );
    }

    private load(): void {
        const id = this.taskId!;
        this._email.getTaskProjectCandidates(id).subscribe(
            res => {
                this.candidates = (res && res.responseData) || [];
                this.hasEvidence = this.candidates.some((c) => (c.tier || 0) > 0);
                this.hasCandidatesChange.emit(this.candidates.length > 0);
                this._cdr.markForCheck();
            },
            () => {
                this.candidates = [];
                this.hasEvidence = false;
                this.hasCandidatesChange.emit(false);
                this._cdr.markForCheck();
            }
        );
    }
}
